'use client';

import { useState } from 'react';
import { X, MessageCircle, Phone, Calendar, Mail, Send, Check, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  getInquiryAgent,
  parsePrice,
  formatPhoneForWhatsApp,
  formatPhoneForTel
} from '@/lib/inquiry-routing';

interface PropertyInquiryContactProps {
  property: {
    id: string;
    title: string;
    rentalPriceMonthly?: string | number;
    price?: string | number;
    listingType?: string;
    referenceNumber?: string;
  };
}

type ContactMethod = 'whatsapp' | 'viber' | 'facebook' | 'schedule' | 'inquiry';

export default function PropertyInquiryContact({ property }: PropertyInquiryContactProps) {
  // ROUTING LOGIC: Determine correct agent based on property type and price
  const propertyPrice = parsePrice(property.price || property.rentalPriceMonthly);
  const agent = getInquiryAgent(property.listingType || '', propertyPrice);
  const agentPhone = agent.phone;
  const agentName = agent.name;

  const [selectedMethod, setSelectedMethod] = useState<ContactMethod | null>(null);

  // Load saved customer details from localStorage on mount
  const getSavedCustomerDetails = () => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('spire_customer_details');
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error('Failed to load saved customer details:', e);
      }
    }
    return null;
  };

  const savedDetails = getSavedCustomerDetails();

  const [formData, setFormData] = useState({
    name: savedDetails?.name || '',
    email: savedDetails?.email || '',
    phone: savedDetails?.phone || '',
    lookingFor: savedDetails?.lookingFor || 'myself',
    message: `Hi! I'm interested in ${property.title}${property.referenceNumber ? ` (${property.referenceNumber})` : ''}. Please provide more details.`
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const contactMethods = [
    {
      id: 'whatsapp' as ContactMethod,
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#1FAD4F]',
      description: 'Chat with us instantly'
    },
    {
      id: 'viber' as ContactMethod,
      label: 'Viber',
      icon: Phone,
      color: 'bg-[#7360F2] hover:bg-[#5A4DC2]',
      description: 'Call or message on Viber'
    },
    {
      id: 'facebook' as ContactMethod,
      label: 'Facebook',
      icon: MessageCircle,
      color: 'bg-[#1877F2] hover:bg-[#145DBF]',
      description: 'Message us on Messenger'
    },
    {
      id: 'schedule' as ContactMethod,
      label: 'Schedule Viewing',
      icon: Calendar,
      color: 'bg-[#b50400] hover:bg-[#b50400]',
      description: 'Book a property tour'
    },
    {
      id: 'inquiry' as ContactMethod,
      label: 'Send Inquiry',
      icon: Mail,
      color: 'bg-[#d60500] hover:bg-[#b50400]',
      description: 'Get detailed information'
    },
  ];

  const handleContactMethod = async (method: ContactMethod) => {
    // Always show form first to record inquiry
    setSelectedMethod(method);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save customer details to localStorage for future inquiries
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('spire_customer_details', JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            lookingFor: formData.lookingFor,
            savedAt: new Date().toISOString(),
          }));
        } catch (e) {
          console.error('Failed to save customer details:', e);
        }
      }

      // Determine inquiry type based on selected method
      let inquiryType = 'General Inquiry';
      let preferredContact = 'WhatsApp';

      if (selectedMethod === 'schedule') {
        inquiryType = 'Schedule Viewing';
      } else if (selectedMethod === 'whatsapp') {
        preferredContact = 'WhatsApp';
      } else if (selectedMethod === 'viber') {
        preferredContact = 'Viber';
      } else if (selectedMethod === 'facebook') {
        preferredContact = 'Facebook Messenger';
      }

      const lookingForLabel = formData.lookingFor === 'myself' ? 'Looking for Myself' : 'I am Broker';

      const response = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: property.id,
          propertyTitle: property.title,
          referenceNumber: property.referenceNumber,
          listingType: property.listingType,
          price: propertyPrice, // Send parsed numeric price for routing
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          lookingFor: formData.lookingFor,
          preferredContact: preferredContact,
          inquiryType: inquiryType,
          message: formData.message,
          source: 'property-page'
        })
      });

      if (response.ok) {
        setShowThankYou(true);

        // After form submission, open the selected communication channel
        setTimeout(() => {
          const baseMessage = `New ${inquiryType}\n\nProperty: ${property.title}${property.referenceNumber ? `\nCode: ${property.referenceNumber}` : ''}\n\nContact:\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nType: ${lookingForLabel}\nPreferred: ${preferredContact}\n\nMessage:\n${formData.message}`;

          if (selectedMethod === 'whatsapp') {
            window.open(`https://wa.me/${formatPhoneForWhatsApp(agentPhone)}?text=${encodeURIComponent(baseMessage)}`, '_blank');
          } else if (selectedMethod === 'viber') {
            window.open(`viber://chat?number=${encodeURIComponent(formatPhoneForTel(agentPhone))}`, '_blank');
          } else if (selectedMethod === 'facebook') {
            window.open('https://m.me/469634980460313', '_blank');
          } else {
            // For schedule and inquiry, send WhatsApp notification to assigned agent
            window.open(`https://wa.me/${formatPhoneForWhatsApp(agentPhone)}?text=${encodeURIComponent(baseMessage)}`, '_blank');
          }

          // Close after 2 seconds
          setTimeout(() => {
            setSelectedMethod(null);
            setShowThankYou(false);
            setFormData({
              name: '',
              email: '',
              phone: '',
              lookingFor: 'myself',
              message: `Hi! I'm interested in ${property.title}${property.referenceNumber ? ` (${property.referenceNumber})` : ''}. Please provide more details.`
            });
          }, 2000);
        }, 1500);
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="border-2 border-[#d60500] shadow-lg">
        <CardContent className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Us About This Property</h3>
          <p className="text-gray-600 mb-6">Choose your preferred way to get in touch</p>

          <div className="grid grid-cols-1 gap-3">
            {contactMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => handleContactMethod(method.id)}
                className={`${method.color} text-white p-4 rounded-xl transition-all hover:scale-105 shadow-md hover:shadow-lg flex items-center justify-between group`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <method.icon className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">{method.label}</div>
                    <div className="text-xs text-white/80">{method.description}</div>
                  </div>
                </div>
                <svg
                  className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            We respond within minutes during business hours
          </p>
        </CardContent>
      </Card>

      {/* Inquiry Form Modal - Shows for ALL contact methods */}
      {selectedMethod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedMethod(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {showThankYou ? (
              // Thank You Screen
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#b50400]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
                <p className="text-gray-600">
                  {selectedMethod === 'schedule'
                    ? "We've received your viewing request. We'll contact you via WhatsApp to confirm the schedule."
                    : "We've received your inquiry. We'll contact you via WhatsApp shortly."}
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-[#d60500] to-[#b50400]">
                  <h2 className="text-2xl font-bold text-white">
                    {selectedMethod === 'schedule' && 'Schedule a Viewing'}
                    {selectedMethod === 'whatsapp' && 'WhatsApp Inquiry'}
                    {selectedMethod === 'viber' && 'Viber Inquiry'}
                    {selectedMethod === 'facebook' && 'Facebook Inquiry'}
                    {selectedMethod === 'inquiry' && 'Send Inquiry'}
                  </h2>
                  <button
                    onClick={() => setSelectedMethod(null)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Property Info */}
                <div className="p-6 bg-gray-50 border-b">
                  <h3 className="font-semibold text-gray-900 mb-1">{property.title}</h3>
                  {property.referenceNumber && (
                    <p className="text-sm text-gray-600">Property Code: {property.referenceNumber}</p>
                  )}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                        placeholder="Juan Dela Cruz"
                      />
                    </div>
                  </div>

                  {/* Looking For */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      I am <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer flex-1">
                        <input
                          type="radio"
                          name="lookingFor"
                          value="myself"
                          checked={formData.lookingFor === 'myself'}
                          onChange={(e) => setFormData({ ...formData, lookingFor: e.target.value })}
                          className="w-4 h-4 text-[#d60500] focus:ring-[#d60500]"
                        />
                        <span className="text-sm text-gray-700">Looking for Myself</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer flex-1">
                        <input
                          type="radio"
                          name="lookingFor"
                          value="broker"
                          checked={formData.lookingFor === 'broker'}
                          onChange={(e) => setFormData({ ...formData, lookingFor: e.target.value })}
                          className="w-4 h-4 text-[#d60500] focus:ring-[#d60500]"
                        />
                        <span className="text-sm text-gray-700">I am Broker</span>
                      </label>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                        placeholder="juan@example.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent"
                        placeholder="+63 912 345 6789"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {selectedMethod === 'schedule' ? 'Preferred Date/Time' : 'Message'} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d60500] focus:border-transparent resize-none"
                      placeholder={selectedMethod === 'schedule' ? 'e.g., January 30, 2026 at 2:00 PM' : 'Tell us about your requirements...'}
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#d60500] hover:bg-[#b50400] text-white h-12 text-base font-semibold"
                  >
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {selectedMethod === 'schedule' ? 'Request Viewing' : 'Send Inquiry'}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to be contacted via WhatsApp for this {selectedMethod === 'schedule' ? 'viewing' : 'inquiry'}.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
